import React, { useEffect, useState } from "react";
import {
    Key,
    Shield,
    Calendar,
    Settings,
    Server,
    Network,
    Hash,
    Clock,
    RefreshCw,
    Cpu,
    Users,
    Layers,
    Tag,
} from "lucide-react";
import baseapi from "../../../services/baseapi";
import { showError } from "../../../utils/toast";

interface LicenseInfoProps {
    agent_id: string;
    organization_id: string;
    asset_id: string;
    client_id: string;
}

const LicenseInfo: React.FC<LicenseInfoProps> = ({
    agent_id,
    organization_id,
    asset_id,
    client_id,
}) => {
    const [licenseInformation, setLicenseInformation] = useState<any>(null);
    const [loading, setLoading] = useState(true);

    async function fetchLicense() {
        setLoading(true);
        try {
            const params = {
                query: JSON.stringify({
                    client_id,
                    organization_id,
                    agent_id,
                    asset_id
                }),
            };
            const response = await baseapi.get("/asset_windows_activation", { params });
            if (response.data?.data?.[0]?.results) {
                const item = response.data?.data?.[0];
                const rawResults = item?.results;

                if (rawResults) {
                    const results =
                        typeof rawResults === "string"
                            ? JSON.parse(rawResults)
                            : rawResults;

                    const parsed = {
                        ActivationExpiration: results?.ActivationExpiration || {},
                        BasicLicenseInformation: results?.BasicLicenseInformation || {},
                        KMSClientInformation: results?.KMSClientInformation || {},
                        KMSHostInformation: results?.KMSHostInformation || {},
                    };
                    setLicenseInformation(parsed);
                }
            }
        } catch (e: any) {
            showError(e.message);
        }
        setLoading(false);
    }

    useEffect(() => {
        fetchLicense();
    }, [agent_id, asset_id, organization_id, client_id]);

    if (loading) {
        return <div className="text-slate-600 dark:text-gray-200 text-xs p-4">Loading license information...</div>;
    }

    const basicInfo = licenseInformation?.BasicLicenseInformation || {};
    const expiration = licenseInformation?.ActivationExpiration || {};
    const kmsClient = licenseInformation?.KMSClientInformation || {};
    const kmsHost = licenseInformation?.KMSHostInformation || {};

    return (
        <div className="p-1 space-y-3">
            {/* Compact Header */}
            <div className="flex items-center justify-between border-b border-slate-200 dark:border-gray-700 pb-2 mb-2">
                <div className="flex items-center">
                    <div className="p-1.5 bg-orange-600 rounded mr-2.5 shadow-sm">
                        <Key className="w-4 h-4 text-white" />
                    </div>
                    <div>
                        <h2 className="text-sm font-bold text-slate-900 dark:text-white leading-tight">Windows License Information</h2>
                        <p className="text-[10px] text-slate-500 dark:text-gray-200 uppercase tracking-wider font-semibold">
                            {basicInfo.licensestatus || 'Status Unknown'}
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => fetchLicense()}
                        disabled={loading}
                        className="p-1.5 bg-white dark:bg-gray-800 hover:bg-slate-50 dark:hover:bg-gray-700 text-slate-600 dark:text-gray-300 border border-slate-200 dark:border-gray-600 rounded-md transition-colors disabled:opacity-50 cursor-pointer shadow-sm dark:shadow-none"
                        title="Refresh data"
                    >
                        <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                    </button>
                </div>
            </div>

            {licenseInformation ? (
                <div className="space-y-3">
                    {/* Status Overview Grid */}
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                        <div className={`rounded-md border p-2 flex items-center justify-between shadow-sm dark:shadow-none ${basicInfo.licensestatus === 'Licensed'
                            ? 'bg-green-50 dark:bg-green-500/5 border-green-200 dark:border-green-500/20' : 'bg-red-50 dark:bg-red-500/5 border-red-200 dark:border-red-500/20'
                            }`}>
                            <div className="min-w-0">
                                <p className="text-[10px] text-slate-500 dark:text-gray-200 font-medium">License Status</p>
                                <p className={`text-xs font-bold truncate ${basicInfo.licensestatus === 'Licensed'
                                    ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                                    }`}>{basicInfo.licensestatus || "-"}</p>
                            </div>
                            <Shield className={`w-4 h-4 shrink-0 ${basicInfo.licensestatus === 'Licensed'
                                ? 'text-green-600 dark:text-green-500' : 'text-red-600 dark:text-red-500'
                                }`} />
                        </div>

                        <div className="bg-blue-50 dark:bg-blue-500/5 border border-blue-200 dark:border-blue-500/20 rounded-md p-2 flex items-center justify-between shadow-sm dark:shadow-none">
                            <div className="min-w-0">
                                <p className="text-[10px] text-slate-500 dark:text-gray-200 font-medium">Days Remaining</p>
                                <p className="text-slate-900 dark:text-white text-xs font-bold truncate">
                                    {expiration.remaining_days || '-'}
                                </p>
                            </div>
                            <Calendar className="w-4 h-4 text-blue-600 dark:text-blue-500 shrink-0" />
                        </div>

                        <div className="bg-purple-50 dark:bg-purple-500/5 border border-purple-200 dark:border-purple-500/20 rounded-md p-2 flex items-center justify-between shadow-sm dark:shadow-none">
                            <div className="min-w-0">
                                <p className="text-[10px] text-slate-500 dark:text-gray-200 font-medium">Activation Type</p>
                                <p className="text-slate-900 dark:text-white text-xs font-bold truncate">
                                    {basicInfo.configuredactivationtype || "-"}
                                </p>
                            </div>
                            <Settings className="w-4 h-4 text-purple-600 dark:text-purple-500 shrink-0" />
                        </div>

                        <div className="bg-cyan-50 dark:bg-cyan-500/5 border border-cyan-200 dark:border-cyan-500/20 rounded-md p-2 flex items-center justify-between shadow-sm dark:shadow-none">
                            <div className="min-w-0">
                                <p className="text-[10px] text-slate-500 dark:text-gray-200 font-medium">KMS Host</p>
                                <p className="text-cyan-700 dark:text-cyan-300 text-xs font-bold truncate">
                                    {kmsHost.is_kms_host || "-"}
                                </p>
                            </div>
                            <Cpu className="w-4 h-4 text-cyan-600 dark:text-cyan-400 shrink-0" />
                        </div>

                        <div className="bg-emerald-50 dark:bg-emerald-500/5 border border-emerald-200 dark:border-emerald-500/20 rounded-md p-2 flex items-center justify-between shadow-sm dark:shadow-none">
                            <div className="min-w-0">
                                <p className="text-[10px] text-slate-500 dark:text-gray-200 font-medium">Client Count</p>
                                <p className="text-emerald-700 dark:text-emerald-300 text-xs font-bold truncate">
                                    {kmsHost.current_count || "-"}
                                </p>
                            </div>
                            <Users className="w-4 h-4 text-emerald-600 dark:text-emerald-400 shrink-0" />
                        </div>
                    </div>

                    {/* Basic License Details */}
                    <div className="bg-white dark:bg-gray-800/40 rounded-md border border-slate-200 dark:border-gray-700 p-2.5 shadow-sm dark:shadow-none">
                        <div className="flex items-center gap-2 mb-2 border-b border-slate-100 dark:border-gray-700/50 pb-1.5">
                            <Key className="w-3.5 h-3.5 text-orange-500" />
                            <h3 className="text-[11px] font-bold text-slate-900 dark:text-white uppercase tracking-wide">Basic License Information</h3>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-[11px] bg-slate-50 dark:bg-gray-900 p-1 border rounded-md border-slate-200 dark:border-gray-700/30">
                            <div className="space-y-1.5">
                                <div className="flex justify-between items-center p-1.5 rounded dark:border-transparent">
                                    <span className="text-slate-600 dark:text-gray-200 flex items-center gap-1.5"><div className="w-1.5 h-1.5 bg-yellow-400 rounded-full"></div> Product</span>
                                    <span className="text-slate-900 dark:text-white font-medium truncate max-w-[180px]">{basicInfo.name || "-"}</span>
                                </div>
                                <div className="flex justify-between items-center p-1.5 rounded dark:border-transparent">
                                    <span className="text-slate-600 dark:text-gray-200 flex items-center gap-1.5"><div className="w-1.5 h-1.5 bg-blue-400 rounded-full"></div> Description</span>
                                    <span className="text-slate-900 dark:text-white truncate max-w-[180px]" title={basicInfo.description}>{basicInfo.description || "-"}</span>
                                </div>
                                <div className="flex justify-between items-center p-1.5 rounded dark:border-transparent">
                                    <span className="text-slate-600 dark:text-gray-200 flex items-center gap-1.5"><div className="w-1.5 h-1.5 bg-green-400 rounded-full"></div> Partial Key</span>
                                    <span className="text-slate-900 dark:text-white font-mono bg-slate-200 dark:bg-gray-800 px-1.5 rounded text-[10px]">{basicInfo.partial_product_key ? `****-${basicInfo.partial_product_key}` : "-"}</span>
                                </div>
                                <div className="flex justify-between items-center p-1.5 rounded dark:border-transparent">
                                    <span className="text-slate-600 dark:text-gray-200 flex items-center gap-1.5"><Hash className="w-3 h-3 text-amber-500 dark:text-amber-400" /> Activation ID</span>
                                    <span className="text-slate-900 dark:text-white font-mono text-[10px] truncate max-w-[180px]" title={basicInfo.activation_id}>{basicInfo.activation_id || "-"}</span>
                                </div>
                            </div>
                            <div className="space-y-1.5">
                                <div className="flex justify-between items-center p-1.5 rounded dark:border-transparent">
                                    <span className="text-slate-600 dark:text-gray-200 flex items-center gap-1.5"><Tag className="w-3 h-3 text-cyan-600 dark:text-cyan-400" /> Key Channel</span>
                                    <span className="text-cyan-700 dark:text-cyan-300 font-medium">{basicInfo.product_key_channel || "-"}</span>
                                </div>
                                <div className="flex justify-between items-center p-1.5 rounded dark:border-transparent">
                                    <span className="text-slate-600 dark:text-gray-200 flex items-center gap-1.5"><Layers className="w-3 h-3 text-orange-500 dark:text-orange-400" /> Rearm Count</span>
                                    <span className="text-slate-900 dark:text-white font-mono">{basicInfo.remaining_rearm_count !== undefined ? basicInfo.remaining_rearm_count : "-"}</span>
                                </div>
                                <div className="flex justify-between items-center p-1.5 rounded dark:border-transparent">
                                    <span className="text-slate-600 dark:text-gray-200 flex items-center gap-1.5"><div className="w-1.5 h-1.5 bg-purple-400 rounded-full"></div> Activation</span>
                                    <span className="text-slate-900 dark:text-white">{basicInfo.configuredactivationtype || "-"}</span>
                                </div>
                                <div className="flex justify-between items-center p-1.5 rounded dark:border-transparent">
                                    <span className="text-slate-600 dark:text-gray-200 flex items-center gap-1.5"><Calendar className="w-3 h-3 text-cyan-600 dark:text-cyan-400" /> Expiration</span>
                                    <span className="text-slate-900 dark:text-white text-[10px]">{expiration.expiration_date || "-"}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* KMS Client & Host Information */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="bg-white dark:bg-gray-800/40 rounded-md border border-slate-200 dark:border-gray-700 p-2.5 shadow-sm dark:shadow-none">
                            <div className="flex items-center gap-2 mb-2 border-b border-slate-100 dark:border-gray-700/50 pb-1.5">
                                <Server className="w-3.5 h-3.5 text-orange-500" />
                                <h3 className="text-[11px] font-bold text-slate-900 dark:text-white uppercase tracking-wide">KMS & Host Information</h3>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-[11px] bg-slate-50 dark:bg-gray-900 p-1 border rounded-md border-slate-200 dark:border-gray-700/30">
                                <div className="space-y-1.5">
                                    <div className="p-1.5 rounded dark:border-transparent">
                                        <span className="text-slate-600 dark:text-gray-200 flex items-center gap-1.5 mb-0.5"><Network className="w-3 h-3 text-blue-500 dark:text-blue-400" /> KMS Server</span>
                                        <p className="text-slate-900 dark:text-white font-mono truncate">{kmsClient.registeredkms_machine_name || "-"}</p>
                                    </div>
                                    <div className="p-1.5 rounded dark:border-transparent">
                                        <span className="text-slate-600 dark:text-gray-200 flex items-center gap-1.5 mb-0.5"><Hash className="w-3 h-3 text-purple-500 dark:text-purple-400" /> Machine ID</span>
                                        <p className="text-[10px] text-slate-600 dark:text-gray-200 break-all leading-tight">{kmsClient.client_machine_id || "-"}</p>
                                    </div>
                                    <div className="flex justify-between items-center p-1.5 rounded dark:border-transparent">
                                        <span className="text-slate-600 dark:text-gray-200 flex items-center gap-1.5"><Cpu className="w-3 h-3 text-cyan-600 dark:text-cyan-400" /> KMS Host</span>
                                        <span className="text-cyan-700 dark:text-cyan-300 font-bold">{kmsHost.is_kms_host || "-"}</span>
                                    </div>
                                </div>
                                <div className="space-y-1.5">
                                    <div className="flex justify-between items-center p-1.5 rounded dark:border-transparent">
                                        <span className="text-slate-600 dark:text-gray-200 flex items-center gap-1.5"><Users className="w-3 h-3 text-emerald-600 dark:text-emerald-400" /> Client Count</span>
                                        <span className="text-emerald-700 dark:text-emerald-300 font-bold">{kmsHost.current_count || "-"}</span>
                                    </div>
                                    <div className="flex justify-between items-center p-1.5 rounded dark:border-transparent">
                                        <span className="text-slate-600 dark:text-gray-200 flex items-center gap-1.5"><Clock className="w-3 h-3 text-green-600 dark:text-green-400" /> Activation</span>
                                        <span className="text-slate-900 dark:text-white">{kmsClient.activation_interval || "-"}</span>
                                    </div>
                                    <div className="flex justify-between items-center p-1.5 rounded dark:border-transparent">
                                        <span className="text-slate-600 dark:text-gray-200 flex items-center gap-1.5"><Clock className="w-3 h-3 text-orange-600 dark:text-orange-400" /> Renewal</span>
                                        <span className="text-slate-900 dark:text-white">{kmsClient.renewal_interval || "-"}</span>
                                    </div>
                                    <div className="flex justify-between items-center p-1.5 rounded dark:border-gray-700/50">
                                        <span className="text-slate-600 dark:text-gray-200 flex items-center gap-1.5"><Settings className="w-3 h-3 text-cyan-600 dark:text-cyan-400" /> Caching</span>
                                        <span className={`text-[9px] font-bold px-1.5 rounded py-0.5 border ${kmsClient.kms_host_caching === 'Enabled'
                                            ? 'text-green-600 dark:text-green-400 border-green-200 dark:border-green-500/20 bg-green-50 dark:bg-green-500/10'
                                            : 'text-red-600 dark:text-red-400 border-red-200 dark:border-red-500/20 bg-red-50 dark:bg-red-500/10'
                                            }`}>{kmsClient.kms_host_caching || "-"}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Activation Timeline */}
                        <div className="bg-white dark:bg-gray-800/40 rounded-md border border-slate-200 dark:border-gray-700 p-2.5 shadow-sm dark:shadow-none">
                            <div className="flex items-center gap-2 mb-2 border-b border-slate-100 dark:border-gray-700/50 pb-1.5">
                                <Calendar className="w-3.5 h-3.5 text-orange-500" />
                                <h3 className="text-[11px] font-bold text-slate-900 dark:text-white uppercase tracking-wide">Activation Timeline</h3>
                            </div>
                            <div className="p-2 bg-slate-50 dark:bg-gray-900 p-1 border rounded-md border-slate-200 dark:border-gray-700/30">
                                <div className="flex justify-between items-end mb-1.5 text-[10px]">
                                    <div className="flex flex-col">
                                        <span className="text-slate-500 dark:text-gray-200 uppercase font-semibold">Remaining</span>
                                        <span className="text-slate-900 dark:text-white text-sm font-bold leading-tight">{expiration.remaining_days !== undefined && expiration.remaining_days !== null ? String(expiration.remaining_days) : "-"}</span>
                                    </div>
                                    <div className="text-right flex flex-col">
                                        <span className="text-slate-500 dark:text-gray-200 uppercase font-semibold">Expires on</span>
                                        <span className="text-slate-900 dark:text-white font-mono">{expiration.expiration_date || "-"}</span>
                                    </div>
                                </div>
                                {(() => {
                                    const remDaysStr = expiration.remaining_days !== undefined && expiration.remaining_days !== null ? String(expiration.remaining_days) : "";
                                    const remDaysNum = parseInt(remDaysStr, 10);
                                    const isPermanentOrLong = remDaysStr.toLowerCase().includes('permanent') || (!isNaN(remDaysNum) && remDaysNum > 365);
                                    const progressPercent = isPermanentOrLong ? 100 : Math.max(((!isNaN(remDaysNum) ? remDaysNum : 0) / 365) * 100, 5);
                                    return (
                                        <div className="w-full bg-slate-200 dark:bg-gray-700 rounded-full h-1.5 overflow-hidden">
                                            <div
                                                className="bg-green-500 h-full transition-all duration-500"
                                                style={{ width: `${progressPercent}%` }}
                                            ></div>
                                        </div>
                                    );
                                })()}
                                <div className="flex justify-between text-[9px] text-slate-500 dark:text-gray-200 mt-1 uppercase font-medium">
                                    <span>Expired</span>
                                    <span>1 Year+</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="text-center py-8 bg-white dark:bg-gray-800/20 rounded-md border border-slate-200 dark:border-gray-700/50 shadow-sm dark:shadow-none">
                    <Key className="w-6 h-6 text-slate-400 dark:text-gray-600 mx-auto mb-2 opacity-30" />
                    <h3 className="text-xs font-semibold text-yellow-600 dark:text-yellow-300 mb-1">No License Information</h3>
                    <p className="text-[10px] text-slate-500 dark:text-gray-200">Not available for this system.</p>
                </div>
            )}
        </div>
    );
};

export default LicenseInfo;
